import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getAccessToken } from "api/baseApi";
import { makeFullPath } from "api/path";
import DialogTitleStyled from "components/shared/dialog/dialog-title-styled";
import type { DialogBaseProps } from "shared/dialog/types";
import { useToast } from "shared/toast/hooks/use-toast";
import { useDownloadFile } from "shared/editors/hooks/use-download-file";

interface Props extends DialogBaseProps {
  imageUrl: string;
  fileName: string;
  documentId?: string;
}

const isSavedDocumentId = (id?: string) => !!id && !id.startsWith("temp-");

export const ImageViewerDialog = ({ isOpen, onClose, imageUrl, fileName, documentId }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const downloadFile = useDownloadFile();
  const [fullImageUrl, setFullImageUrl] = useState<string | null>(null);
  const [isLoadingFull, setIsLoadingFull] = useState(false);
  const tRef = useRef(t);
  const toastRef = useRef(toast);
  tRef.current = t;
  toastRef.current = toast;

  useEffect(() => {
    if (!isOpen || !isSavedDocumentId(documentId)) {
      return;
    }

    let cancelled = false;
    let createdObjectUrl: string | null = null;
    const controller = new AbortController();

    const load = async () => {
      setIsLoadingFull(true);
      try {
        const token = await getAccessToken();
        const response = await fetch(makeFullPath(`/api/documents/${documentId}`), {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Unexpected status ${response.status}`);
        const blob = await response.blob();
        if (cancelled) return;
        createdObjectUrl = URL.createObjectURL(blob);
        setFullImageUrl(createdObjectUrl);
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        toastRef.current.error(tRef.current("dashboard.extraWorkAgreements.create.image.loadError"));
      } finally {
        if (!cancelled) setIsLoadingFull(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
      controller.abort();
      setFullImageUrl(null);
      if (createdObjectUrl) {
        // Defer revoke so the <img> can detach from the blob URL before it becomes invalid.
        setTimeout(() => URL.revokeObjectURL(createdObjectUrl!), 0);
      }
    };
  }, [isOpen, documentId]);

  const handleDownload = () => {
    if (!isSavedDocumentId(documentId)) return;
    try {
      downloadFile(makeFullPath(`/api/documents/${documentId}`), fileName);
    } catch {
      toast.error(t("content.overview.documents.downloadError"));
    }
  };

  const displayedUrl = fullImageUrl ?? imageUrl;

  return (
    <Dialog
      maxWidth="xl"
      fullWidth
      open={isOpen}
      onClose={onClose}
      PaperProps={{ sx: { width: "95vw", height: "95vh", maxWidth: "none", m: 0 } }}
    >
      <DialogTitleStyled title={fileName} onClose={onClose} isOpen={isOpen} />
      <DialogContent sx={{ display: "flex", flexDirection: "column", p: 2 }}>
        <Box
          sx={{
            position: "relative",
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 300,
            backgroundColor: "grey.100",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <img
            src={displayedUrl}
            alt={fileName}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              display: "block",
            }}
          />
          {isLoadingFull && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255,255,255,0.4)",
              }}
            >
              <CircularProgress size={40} />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {isSavedDocumentId(documentId) && (
          <Button variant="contained" color="primary" startIcon={<FileDownloadOutlinedIcon />} onClick={handleDownload}>
            {t("dashboard.extraWorkAgreements.create.image.download")}
          </Button>
        )}
        <Button variant="outlined" onClick={onClose}>
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
